/**
 * Jackpot Panel Animation Manager
 * Handles font resizing for jackpot panels with 4 levels (grand, major, minor, mini)
 */

import { idleManager } from './IdleManager';

interface JackpotPanelData {
  id: string;
  jackpotName: string;
  levelKey: string; // 'grand', 'major', 'minor', 'mini'
  levelNumber: number; // 1, 2, 3, 4
  currency: string;
  denomination: number;
  element: HTMLElement;
  numberElement: HTMLSpanElement;
  currencyElement: HTMLSpanElement;
  containerElement: HTMLDivElement;
  currentAnimatedValue: number;
  targetValue: number;
  lastUpdateTime: number;
  lastCalculatedLength: number;
  isVisible: boolean;
  visibilityRatio: number; // 0-1, how much of the element is visible
  needsFontResize: boolean;
  // Animation control
  hasReceivedFirstValue: boolean;
  shouldAnimate: boolean;
  // Dynamic speed animation
  valueDifference: number;
  lastAnimationUpdate: number;
}

export class JackpotPanelAnimationManager {
  private panels = new Map<string, JackpotPanelData>();
  private animationId: number | null = null;
  private isRunning = false;
  private intersectionObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private lastFrameTime = 0;
  private readonly TARGET_FPS = 30;
  private readonly FRAME_INTERVAL = 1000 / this.TARGET_FPS;
  private fontResizeQueue = new Set<string>();

  constructor() {
    this.setupIntersectionObserver();
    this.setupResizeObserver();
    // Store subscription handled by main JackpotAnimationManager
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const panelId = entry.target.getAttribute('data-panel-id');
          if (panelId) {
            const panel = this.panels.get(panelId);
            if (panel) {
              panel.isVisible = entry.isIntersecting;
              panel.visibilityRatio = entry.intersectionRatio;

              if (entry.isIntersecting && panel.needsFontResize) {
                this.updateFontSize(panel);
              }

              // Legacy: Animation initialization moved to StandardJackpot component
              // which reads directly from the store via getFormattedValue()
            }
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: [0, 1.0], // Only need enter/leave detection for font resize triggering
      }
    );
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const panelId = entry.target.getAttribute('data-panel-id');
        if (panelId) {
          const panel = this.panels.get(panelId);
          if (panel) {
            panel.needsFontResize = true;
            if (panel.isVisible) {
              this.updateFontSize(panel);
            } else {
              this.fontResizeQueue.add(panelId);
              if (this.isRunning && !this.animationId) {
                this.animationId = requestAnimationFrame(this.animationLoop);
              }
            }
          }
        }
      });
    });
  }

  // Store subscription and target value updates handled by main JackpotAnimationManager

  // Animation logic moved to main JackpotAnimationManager
  // This class now only handles panel-specific font resizing

  private updateFontSize(panel: JackpotPanelData) {
    const { containerElement, numberElement, currencyElement } = panel;

    // Reset font size to measure correctly
    numberElement.style.fontSize = '';
    const initialComputedFontSize = parseFloat(getComputedStyle(numberElement).fontSize);

    // Get the container's computed style to check for padding
    const containerStyle = getComputedStyle(containerElement);
    const paddingRight = parseFloat(containerStyle.paddingRight) || 0;
    const paddingLeft = parseFloat(containerStyle.paddingLeft) || 0;

    const containerWidth = containerElement.clientWidth;
    const currencyWidth = currencyElement.offsetWidth;
    // Account for existing padding-right (for icons) and padding-left, plus small safety margin
    const availableWidth = containerWidth - currencyWidth - paddingRight - paddingLeft - 4;

    const numberWidth = numberElement.scrollWidth;

    if (numberWidth <= availableWidth || availableWidth <= 0) {
      numberElement.style.fontSize = `${initialComputedFontSize}px`;
      // Update visibility immediately
      numberElement.style.visibility = "visible";
      return;
    }

    // Calculate ideal font size
    const newFontSize = (availableWidth / numberWidth) * initialComputedFontSize;
    numberElement.style.fontSize = `${Math.max(newFontSize, 8)}px`;

    panel.lastCalculatedLength = numberElement.innerText.length;
    panel.needsFontResize = false;
    // Update visibility immediately
    numberElement.style.visibility = "visible";
  }

  private processFontResizeQueue() {
    const batch = Array.from(this.fontResizeQueue).slice(0, 3);

    batch.forEach((panelId) => {
      const panel = this.panels.get(panelId);
      if (panel && panel.isVisible) {
        this.updateFontSize(panel);
      }
      this.fontResizeQueue.delete(panelId);
    });
  }

  private animationLoop = (currentTime: number) => {
    if (!this.isRunning) return;

    // If nothing in the queue, stop the loop — it will restart when triggerFontResize is called
    if (this.fontResizeQueue.size === 0) {
      this.animationId = null;
      return;
    }

    // Throttle to target FPS
    if (currentTime - this.lastFrameTime < this.FRAME_INTERVAL) {
      this.animationId = requestAnimationFrame(this.animationLoop);
      return;
    }

    this.lastFrameTime = currentTime;

    // Process font resize queue
    this.processFontResizeQueue();

    // Continue only if there's still work
    if (this.fontResizeQueue.size > 0) {
      this.animationId = requestAnimationFrame(this.animationLoop);
    } else {
      this.animationId = null;
    }
  };

  public registerPanel(
    id: string,
    jackpotName: string,
    levelKey: string,
    levelNumber: number,
    currency: string,
    denomination: number,
    element: HTMLElement,
    numberElement: HTMLSpanElement,
    currencyElement: HTMLSpanElement,
    containerElement: HTMLDivElement
  ): void {
    // Remove existing panel if it exists
    this.unregisterPanel(id);

    const panel: JackpotPanelData = {
      id,
      jackpotName,
      levelKey,
      levelNumber,
      currency,
      denomination,
      element,
      numberElement,
      currencyElement,
      containerElement,
      currentAnimatedValue: 0,
      targetValue: 0,
      lastUpdateTime: Date.now(),
      lastCalculatedLength: 0,
      isVisible: true, // Force visibility for panels
      visibilityRatio: 1.0, // Assume fully visible initially
      needsFontResize: true,
      hasReceivedFirstValue: false,
      shouldAnimate: false,
      valueDifference: 0,
      lastAnimationUpdate: Date.now(),
    };

    this.panels.set(id, panel);

    // Don't register with JackpotAnimationManager - panels handle their own values
    // We only need font resizing functionality here

    // Add data attribute for observers
    element.setAttribute('data-panel-id', id);
    containerElement.setAttribute('data-panel-id', id);

    // Start observing
    this.intersectionObserver?.observe(element);
    this.resizeObserver?.observe(containerElement);

    // Start animation loop if not running
    if (!this.isRunning) {
      // Register animation process with IdleManager
      idleManager.registerProcess(
        'jackpot-panel-animation',
        () => this.startAnimation(),
        () => this.stopAnimation()
      );    
    }
  }

  public triggerFontResize(id: string): void {
    const panel = this.panels.get(id);
    if (panel) {
      panel.needsFontResize = true;
      this.fontResizeQueue.add(id);
      // Process immediately if visible
      if (panel.isVisible) {
        this.updateFontSize(panel);
        this.fontResizeQueue.delete(id);
      } else if (this.isRunning && !this.animationId) {
        // Restart the loop if it stopped due to empty queue
        this.animationId = requestAnimationFrame(this.animationLoop);
      }
    }
  }

  public unregisterPanel(id: string): void {
    const panel = this.panels.get(id);
    if (panel) {
      this.intersectionObserver?.unobserve(panel.element);
      this.resizeObserver?.unobserve(panel.containerElement);
      this.fontResizeQueue.delete(id);

      this.panels.delete(id);

      if (this.panels.size === 0) {
        this.stopAnimation();
      }
    }
  }

  public startAnimation(): void {
    if (this.isRunning) return;
    this.isRunning = true;
    this.lastFrameTime = performance.now();
    this.animationId = requestAnimationFrame(this.animationLoop);
  }

  public stopAnimation(): void {
    this.isRunning = false;
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  public destroy(): void {
    this.stopAnimation();

    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();

    this.panels.clear();
    this.fontResizeQueue.clear();
  }
}

// Singleton instance
export const jackpotPanelAnimationManager = new JackpotPanelAnimationManager();

// Debug helper for development
if (process.env['NODE_ENV'] === 'development') {
  window.jackpotPanelAnimationManager = jackpotPanelAnimationManager;
}
